import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import categoryHandler from "@tCentro/Handlers/CENCategoryHandler";
import { CategoriesState } from "@tCentro/Store/Reducer/CategoryCustomReducer";
import styled, { css } from "styled-components";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";

const getCategoriesParameter = {
    maxDepth: 2,
    includeStartCategory: false,
};
const mapStateToProps = (state: CategoriesState) => {
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
        categoriesData: state.categoriesReducer.value,
    };
};

const mapDispatchToProps = {
    loadCategories,
    categoryHandler,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CENSiteMapCategoryStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const siteMapArticlesStyles: CENSiteMapCategoryStyles = {
    heading: {
        css: css`
            text-align: center;
            font-size: 2rem;
            font-family: "Poppins";
        `,
    },
    link: {
        css: css`
            padding: 5px;
        `,
    },
};

const styles = siteMapArticlesStyles;

export const CENSiteMapProductCategories: FC<Props> = ({
    fields,
    categoriesDataView,
    loadCategories,
    categoryHandler,
    categoriesData,
}) => {
    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
            categoryHandler({});
        }
    }, []);

    if (!categoriesData?.categories) {
        return null;
    }
    const cats = categoriesData.categories.map((pLink: CategoryModel) => {
        return (
            <ListItem key={pLink.id}>
                <Link href={pLink.path} {...styles.link}>
                    {pLink.name}
                </Link>
                {pLink.subCategories && pLink.subCategories.length > 0 ? (
                    <CENSiteMapProductSubCategories subCategories={pLink.subCategories} />
                ) : null}
            </ListItem>
        );
    });
    return (
        <StyledWrapper>
            <Typography as="h2" {...styles.heading}>
                {translate("Product Categories")}
            </Typography>
            <UnorderedList>{cats}</UnorderedList>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENSiteMapProductCategories),
    definition: {
        group: "Common",
    },
};

interface CENSiteMapProductSubCategoriesProps {
    subCategories: CategoryModel[];
}

const CENSiteMapProductSubCategories = ({ subCategories }: CENSiteMapProductSubCategoriesProps) => {
    if (!subCategories) {
        return null;
    }

    return (
        <>
            {subCategories.map(category => (
                <SubCatUnorderedList key={category.id}>
                    <ListItem>
                        <Link href={category.path} {...styles.link}>
                            -{category.name}
                        </Link>
                    </ListItem>
                </SubCatUnorderedList>
            ))}
        </>
    );
};

const UnorderedList = styled.ul<InjectableCss>`
    column-count: 4;
    display: block;
    padding: 10px 30px;
    border-radius: 0;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(theme, [
            css`
                column-count: 1;
            `,
            css`
                column-count: 2;
            `,
            css`
                column-count: 3;
            `,
            css`
                column-count: 4;
            `,
            css`
                column-count: 4;
            `,
        ])}
    ${injectCss}
`;

const SubCatUnorderedList = styled.ul<InjectableCss>`
    column-count: 1;
    display: block;
    padding: 0px 30px 10px 30px;
    border-radius: 0;

    ${injectCss}
`;

const ListItem = styled.li`
    list-style: none;
`;

export default widgetModule;
